import { useCallback } from "react";
import { createForm, updateForm } from "../services/formsService";
import type { FormFilingType } from "@/features/wizard/types/wizard.types";
import type { BusinessFormData } from "@/features/wizard/types/business.types";
import type { IndividualFormData } from "@/features/wizard/types/individual.types";
import { handleApiError } from "@/lib/utils/errorHandler";
import { toast } from "sonner";

export interface SaveDraftParams {
  formType: FormFilingType;
  taxYear: number;
  draftId: string | null;
  businessData: BusinessFormData;
  individualData: IndividualFormData;
  /** Business form display name (optional; for update payload when formType is BUSINESS). */
  formName?: string | null;
}

export interface SaveDraftResult {
  formId: string;
}

/**
 * Saves the current wizard data as a draft via the forms API.
 * If draftId is set, updates the existing form; otherwise creates a new form.
 * Returns the form id (for storing in draftId).
 */
export async function saveFormDraft(
  params: SaveDraftParams
): Promise<SaveDraftResult> {
  const { formType, taxYear, draftId, businessData, individualData, formName } = params;

  const payload =
    formType === "BUSINESS"
      ? {
          filingType: "BUSINESS" as const,
          taxYear,
          ...(formName != null && formName !== "" && { formName }),
          ...businessData,
        }
      : { filingType: "INDIVIDUAL" as const, taxYear, ...individualData };

  if (draftId) {
    const updated = await updateForm(draftId, payload);
    const id =
      (updated as { id?: string }).id ??
      (updated as { _id?: string })._id ??
      draftId;
    return { formId: id };
  }

  const created = await createForm({
    filingType: formType,
    taxYear,
    ...(formType === "BUSINESS" ? businessData : individualData),
  });
  const id =
    (created as { id?: string }).id ?? (created as { _id?: string })._id;
  if (!id) {
    throw new Error("Create form did not return an id");
  }
  return { formId: id };
}

export function useFormDraft() {
  const saveDraft = useCallback(
    async (params: SaveDraftParams): Promise<SaveDraftResult | null> => {
      try {
        return await saveFormDraft(params);
      } catch (err) {
        handleApiError(err);
        toast.error("Failed to save draft");
        return null;
      }
    },
    []
  );

  return { saveDraft };
}
